// The viewer's toolbar: zoom out / in and back to the page width, the page shown
// of how many with the previous and the next (below a computer's width: below
// the pages, the Pager), and the download when the host gives one. One row, at the top of the
// pages; on a phone it scrolls sideways rather than wrapping.
import React from "react"
import { twMerge } from "tailwind-merge"
import Icon from "aldoo-ra/Icon"
import { MagnifyingGlassIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, ArrowsRightLeftIcon, ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon, RectangleStackIcon, XMarkIcon } from "@heroicons/react/24/outline"

const Divider = () => <div className="h-5 w-px bg-admin_bordered shrink-0" />

const Tool = ({ title, onClick, disabled, active, children, className = "" }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className={twMerge(
      "h-8 min-w-8 px-1.5 shrink-0 flex items-center justify-center gap-1 rounded-md text-admin_text hover:bg-admin_neutral_1 disabled:opacity-40 disabled:hover:bg-transparent transition-colors",
      active && "bg-admin_neutral_1 text-admin_primary",
      className
    )}
  >
    {children}
  </button>
)

/**
 * The search in the document: the text, how many matches and which one is shown, the previous
 * and the next (Enter: the next, Shift+Enter: the previous; Escape clears it).
 */
function Search({ value, onChange, count, at, onPrev, onNext, texts }) {
  return (
    <div className="flex items-center gap-0.5 shrink-0" data-testid="pdf-search">
      <div className="relative flex items-center">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2 w-4 h-4 text-admin_text_secondary" />
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              e.shiftKey ? onPrev() : onNext()
            }
            if (e.key === "Escape") onChange("")
          }}
          // no placeholder on a phone: cut short, it reads as noise (the magnifier says it all)
          placeholder={texts.search}
          aria-label={texts.search}
          className="h-8 w-[150px] sm:w-[210px] rounded-md border border-admin_bordered bg-admin_paper pl-7 pr-7 text-sm text-admin_text outline-none focus:border-admin_primary [&::-webkit-search-cancel-button]:hidden max-md:placeholder:text-transparent"
          data-testid="pdf-search-input"
        />
        {value && (
          <button type="button" onClick={() => onChange("")} className="absolute right-1.5 text-admin_text_secondary hover:text-admin_text" aria-label="Clear">
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>
      {/* the count and the previous / next only once something is searched: they come after
          the field, so nothing before them moves (the download, if any, stays at the far end) */}
      {value.trim() && (
        <>
          <span className="min-w-[64px] text-center text-xs tabular-nums text-admin_text_secondary select-none" data-testid="pdf-search-count">
            {count ? `${at + 1} / ${count}` : texts.noMatches}
          </span>
          <Tool title={texts.prevMatch} onClick={onPrev} disabled={!count}>
            <ChevronLeftIcon className="w-5 h-5" />
          </Tool>
          <Tool title={texts.nextMatch} onClick={onNext} disabled={!count}>
            <ChevronRightIcon className="w-5 h-5" />
          </Tool>
        </>
      )}
    </div>
  )
}

/** The page shown, of how many, with the previous and the next. */
export function Pager({ page, numPages, onPrev, onNext, texts }) {
  return (
    <div className="flex items-center gap-0.5">
      <Tool title={texts.prevPage} onClick={onPrev} disabled={page <= 1}>
        <ChevronLeftIcon className="w-5 h-5" />
      </Tool>
      <span className="min-w-[56px] text-center text-sm tabular-nums text-admin_text select-none" data-testid="pdf-page">
        {numPages ? `${page} / ${numPages}` : "–"}
      </span>
      <Tool title={texts.nextPage} onClick={onNext} disabled={page >= numPages}>
        <ChevronRightIcon className="w-5 h-5" />
      </Tool>
    </div>
  )
}

// `pagesToggle`: the page thumbnails' switch (a document of more than one page; the thumbnails are a desktop column)
// `search`: { value, onChange, count, at, onPrev, onNext } — the search in the document (once it is loaded)
export default function ToolBar({ zoom, percent, canZoomOut, canZoomIn, onZoomOut, onZoomIn, onFitWidth, page, numPages, onPrev, onNext, onDownload, pagesToggle, pagesShown, onTogglePages, search, texts }) {
  return (
    <div className="h-11 shrink-0 flex items-center gap-1 sm:gap-2 px-2 border-b border-admin_bordered bg-admin_paper overflow-x-auto whitespace-nowrap" data-testid="pdf-toolbar">
      {pagesToggle && (
        <>
          <Tool title={pagesShown ? texts.hidePages : texts.showPages} onClick={onTogglePages} active={pagesShown} className="hidden md:flex" data-testid="pdf-pages-toggle">
            <RectangleStackIcon className="w-5 h-5" />
          </Tool>
          <div className="hidden md:block"><Divider /></div>
        </>
      )}
      <div className="flex items-center gap-0.5">
        <Tool title={texts.zoomOut} onClick={onZoomOut} disabled={!canZoomOut}>
          <MagnifyingGlassMinusIcon className="w-5 h-5" />
        </Tool>
        {/* the percentage: no room for it on a phone or a small tablet */}
        <span className="hidden lg:inline w-12 text-center text-xs tabular-nums text-admin_text_secondary select-none">{percent}%</span>
        <Tool title={texts.zoomIn} onClick={onZoomIn} disabled={!canZoomIn}>
          <MagnifyingGlassPlusIcon className="w-5 h-5" />
        </Tool>
        {/* back to the page width: a plain button (it was drawn pressed at that width and read as a switch) */}
        {/* not on the smallest screens: the search's count and chevrons need the room */}
        <Tool title={texts.fitWidth} onClick={onFitWidth} disabled={zoom === 1} className="hidden sm:flex">
          {/* the CMS's icon (Icon "pdf_fit_width"), the arrows until it has one */}
          <Icon id="pdf_fit_width" recolor className="w-5 h-5" alt={texts.fitWidth} fallback={<ArrowsRightLeftIcon className="w-5 h-5" />} />
        </Tool>
      </div>
      {/* the pages, one of how many: a one-page document has nowhere to go; on a phone and a small tablet they sit below the pages (Pager) */}
      {numPages !== 1 && (
        <div className="hidden lg:flex items-center gap-1 sm:gap-2">
          <Divider />
          <Pager page={page} numPages={numPages} onPrev={onPrev} onNext={onNext} texts={texts} />
        </div>
      )}
      {search && (
        <>
          <div className="hidden sm:block"><Divider /></div>
          <Search {...search} texts={texts} />
        </>
      )}
      {onDownload && (
        <>
          <div className="flex-1" />
          <Tool title={texts.download} onClick={onDownload} className="px-2">
            <ArrowDownTrayIcon className="w-5 h-5" />
            <span className="hidden sm:inline text-sm">{texts.download}</span>
          </Tool>
        </>
      )}
    </div>
  )
}
